import { _js } from '@ifixit/utils/localize';
import type { Guide, Step } from './types';

// Every name the embed's header, rail, and pager show for a screen, derived
// here so they can't disagree. Index space: 0 = intro, 1..N = steps, N+1 = conclusion.

/** The step's section: its own title, else the nearest preceding titled step. */
function sectionTitleForStep(steps: Step[], stepIndex: number): string {
   for (let i = stepIndex; i >= 0; i--) {
      const title = steps[i]?.title?.trim();
      if (title) {
         return title;
      }
   }
   return '';
}

/** Position alone: "Introduction", "Step 5", "Conclusion". */
export function screenName(guide: Guide, index: number): string {
   if (index <= 0) {
      return _js('Introduction');
   }
   if (index > guide.steps.length) {
      return _js('Conclusion');
   }
   // number is the field StepComments deep-links as #s{number}; api.ts sets it
   // on every step, so the label and that link cannot disagree.
   return _js('Step %1', String(guide.steps[index - 1]?.number));
}

/**
 * Position plus the step's OWN title, for the full embed where each card is a
 * step. An untitled step gets a bare "Step 7" rather than borrowing a section.
 */
export function cardLabel(guide: Guide, index: number): string {
   const name = screenName(guide, index);
   const title = guide.steps[index - 1]?.title?.trim();
   return title ? `${name} · ${title}` : name;
}

/** Position plus section: "Step 5 · Tape over any cracks". */
export function screenLabel(guide: Guide, index: number): string {
   const name = screenName(guide, index);
   if (index <= 0 || index > guide.steps.length) {
      return name;
   }
   const title = sectionTitleForStep(guide.steps, index - 1);
   return title ? `${name} · ${title}` : name;
}

/** Header label: "Step 5 of 50 · Tape over any cracks". */
export function headerLabel(guide: Guide, index: number): string {
   if (index <= 0 || index > guide.steps.length) {
      return screenName(guide, index);
   }
   const number = String(guide.steps[index - 1]?.number);
   // Nests two catalogued strings rather than minting a combined one, which
   // would read English until the next extraction round.
   const position = _js('Step %1', _js('%1 of %2', number, String(guide.steps.length)));
   const title = sectionTitleForStep(guide.steps, index - 1);
   return title ? `${position} · ${title}` : position;
}

/** How far through the guide's steps the reader is, as a percentage. */
export function progressPercent(index: number, totalSteps: number): number {
   if (index <= 0 || totalSteps === 0) {
      return 0;
   }
   if (index > totalSteps) {
      return 100;
   }
   return Math.round((index / totalSteps) * 100);
}
